// src/components/resources/tools/generators/SocialMediaMockup/SocialPreview.tsx

'use client'

import type { FC } from 'react'
import {
  Bookmark,
  Camera,
  Globe,
  Heart,
  ImageIcon,
  MessageCircle,
  MoreHorizontal,
  MoreVertical,
  Repeat2,
  Send,
  Share2,
  ThumbsUp,
} from 'lucide-react'
import { clsx } from 'clsx'
import Image from 'next/image'
import { FacebookVerifiedBadge, InstagramVerifiedBadge } from './VerifiedBadges'
import { VerticalVideoPreview } from './VerticalVideoPreview'
import { formatMetric } from './socialMediaFormatting'
import type { PreviewProps } from './types'

/**
 * Social media post preview for the selected platform.
 */
export const SocialPreview: FC<PreviewProps> = ({ config, onInteraction }): JSX.Element => {

  if (config.platform === 'instagram') {
    const currentLikes = config.interactions.liked ? config.metrics.likes + 1 : config.metrics.likes

    return (
      <article className="mx-auto w-full max-w-md overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
        {/* Header */}
        <header className="flex items-center gap-3 px-3 py-2.5">
          <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-purple-500 to-pink-500">
            {config.avatarUrl ? (
              <Image
                src={config.avatarUrl}
                alt={`${config.username} avatar`}
                width={32}
                height={32}
                className="h-full w-full object-cover"
                unoptimized
              />
            ) : (
              <Camera className="h-4 w-4 text-white" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1">
              <span className="text-sm font-semibold text-gray-900 truncate">{config.username}</span>
              {config.verified && <InstagramVerifiedBadge />}
            </div>
            <span className="text-xs text-gray-500 truncate block">{config.subtitle}</span>
          </div>
          <button className="p-1.5 hover:bg-gray-100 rounded-full flex-shrink-0" type="button">
            <MoreHorizontal className="h-5 w-5 text-gray-900" />
          </button>
        </header>

        {/* Image */}
        {config.mediaUrl ? (
          <Image
            src={config.mediaUrl}
            alt="Post media"
            width={800}
            height={500}
            className="w-full object-cover"
            style={{ maxHeight: '500px', height: 'auto' }}
            unoptimized
          />
        ) : (
          <div className="w-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center" style={{ height: '400px' }}>
            <ImageIcon className="h-16 w-16 text-gray-300" />
          </div>
        )}

        {/* Action Bar */}
        <div className="px-3 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                className="hover:opacity-60 transition-opacity"
                onClick={() => onInteraction('liked')}
                type="button"
                aria-label={config.interactions.liked ? 'Unlike' : 'Like'}
              >
                <Heart className={clsx('h-6 w-6', config.interactions.liked ? 'fill-red-500 text-red-500' : 'text-gray-900')} />
              </button>
              <button className="hover:opacity-60 transition-opacity" type="button" aria-label="Comment">
                <MessageCircle className="h-6 w-6 text-gray-900" />
              </button>
              <button className="hover:opacity-60 transition-opacity" type="button" aria-label="Send">
                <Send className="h-6 w-6 text-gray-900" />
              </button>
            </div>
            <button
              className="hover:opacity-60 transition-opacity"
              onClick={() => onInteraction('bookmarked')}
              type="button"
              aria-label={config.interactions.bookmarked ? 'Remove bookmark' : 'Bookmark'}
            >
              <Bookmark className={clsx('h-6 w-6', config.interactions.bookmarked ? 'fill-gray-900 text-gray-900' : 'text-gray-900')} />
            </button>
          </div>

          {/* Likes */}
          {config.showMetrics && (
            <div className="mt-2">
              <span className="text-sm font-semibold text-gray-900">{formatMetric(currentLikes)} likes</span>
            </div>
          )}

          {/* Caption */}
          <div className="mt-1.5">
            <p className="text-sm">
              <span className="font-semibold text-gray-900">{config.username}</span>
              <span className="ml-1.5 text-gray-900">{config.content}</span>
            </p>
            {config.hashtags && (
              <p className="mt-1 text-sm text-blue-900">{config.hashtags}</p>
            )}
          </div>

          {/* View comments */}
          {config.showMetrics && config.metrics.comments > 0 && (
            <button className="mt-1.5 text-sm text-gray-500" type="button">
              View all {formatMetric(config.metrics.comments)} comments
            </button>
          )}

          {/* Timestamp */}
          <div className="mt-1 text-xs text-gray-400 uppercase">
            {config.timestamp}
          </div>
        </div>

        {/* Comment Input */}
        <div className="border-t border-gray-200 px-3 py-2.5 flex items-center gap-3">
          <input
            type="text"
            placeholder="Add a comment..."
            className="flex-1 text-sm outline-none"
            aria-label="Add a comment"
          />
          <button className="text-sm font-semibold text-blue-500 hover:text-blue-700" type="button">
            Post
          </button>
        </div>
      </article>
    )
  }

  if (config.platform === 'x') {
    const currentLikes = config.interactions.liked ? config.metrics.likes + 1 : config.metrics.likes

    return (
      <article className="mx-auto w-full max-w-xl rounded-xl border border-gray-200 bg-white p-4 shadow-xl">
        <div className="flex gap-3">
          {/* Avatar */}
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-200">
            {config.avatarUrl ? (
              <Image
                src={config.avatarUrl}
                alt={`${config.username} avatar`}
                width={40}
                height={40}
                className="h-full w-full object-cover"
                unoptimized
              />
            ) : (
              <ImageIcon className="h-5 w-5 text-gray-400" />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-1 flex-wrap">
                <span className="font-bold text-sm text-gray-900">{config.username}</span>
                {config.verified && (
                  <svg viewBox="0 0 22 22" className="h-4 w-4" role="img" aria-label="Verified">
                    <path fill="#1d9bf0" d="M20.396 11c-.018-.646-.215-1.275-.57-1.816-.354-.54-.852-.972-1.438-1.246.223-.607.27-1.264.14-1.897-.131-.634-.437-1.218-.882-1.687-.47-.445-1.053-.75-1.687-.882-.633-.13-1.29-.083-1.897.14-.273-.587-.704-1.086-1.245-1.44S11.647 1.62 11 1.604c-.646.017-1.273.213-1.813.568s-.969.854-1.24 1.44c-.608-.223-1.267-.272-1.902-.14-.635.13-1.22.436-1.69.882-.445.47-.749 1.055-.878 1.688-.13.633-.08 1.29.144 1.896-.587.274-1.087.705-1.443 1.245-.356.54-.555 1.17-.574 1.817.02.647.218 1.276.574 1.817.356.54.856.972 1.443 1.245-.224.606-.274 1.263-.144 1.896.13.634.433 1.218.877 1.688.47.443 1.054.747 1.687.878.633.132 1.29.084 1.897-.136.274.586.705 1.084 1.246 1.439.54.354 1.17.551 1.816.569.647-.016 1.276-.213 1.817-.567s.972-.854 1.245-1.44c.604.239 1.266.296 1.903.164.636-.132 1.22-.447 1.68-.907.46-.46.776-1.044.908-1.681s.075-1.299-.165-1.903c.586-.274 1.084-.705 1.439-1.246.354-.54.551-1.17.569-1.816zM9.662 14.85l-3.429-3.428 1.293-1.302 2.072 2.072 4.4-4.794 1.347 1.246z" />
                  </svg>
                )}
                <span className="text-sm text-gray-500">{config.handle}</span>
                <span className="text-gray-500">·</span>
                <span className="text-sm text-gray-500">{config.timestamp}</span>
              </div>
              <button className="p-1.5 hover:bg-gray-100 rounded-full" type="button">
                <MoreHorizontal className="h-4 w-4 text-gray-500" />
              </button>
            </div>

            {/* Text Content */}
            <div className="mt-2">
              <p className="text-sm text-gray-900 whitespace-pre-wrap">{config.content}</p>
              {config.hashtags && (
                <p className="mt-2 text-sm text-blue-500">{config.hashtags}</p>
              )}
            </div>

            {/* Media */}
            {config.mediaUrl && (
              <div className="mt-3 overflow-hidden rounded-2xl border border-gray-200">
                <Image
                  src={config.mediaUrl}
                  alt="Post media"
                  width={800}
                  height={500}
                  className="w-full object-cover"
                  style={{ maxHeight: '500px', height: 'auto' }}
                  unoptimized
                />
              </div>
            )}

            {/* Engagement Bar */}
            {config.showMetrics && (
              <div className="mt-3 flex items-center gap-6 text-xs text-gray-500">
                <button
                  className="flex items-center gap-2 hover:text-pink-600 transition-colors group"
                  onClick={() => onInteraction('liked')}
                  type="button"
                  aria-label={config.interactions.liked ? 'Unlike' : 'Like'}
                >
                  <Heart className={clsx('h-4 w-4', config.interactions.liked ? 'fill-pink-600 text-pink-600' : 'group-hover:fill-pink-600')} />
                  <span className={config.interactions.liked ? 'text-pink-600' : ''}>{formatMetric(currentLikes)}</span>
                </button>
                <button className="flex items-center gap-2 hover:text-blue-500 transition-colors group" type="button" aria-label="Comment">
                  <MessageCircle className="h-4 w-4 group-hover:fill-blue-500" />
                  <span>{formatMetric(config.metrics.comments)}</span>
                </button>
                <button className="flex items-center gap-2 hover:text-green-600 transition-colors group" type="button" aria-label="Repost">
                  <Repeat2 className="h-4 w-4" />
                  <span>{formatMetric(config.metrics.shares)}</span>
                </button>
                <button className="flex items-center gap-2 hover:text-blue-500 transition-colors" type="button" aria-label="Share">
                  <Share2 className="h-4 w-4" />
                </button>
                <button
                  className="ml-auto hover:text-blue-500 transition-colors"
                  onClick={() => onInteraction('bookmarked')}
                  type="button"
                  aria-label={config.interactions.bookmarked ? 'Remove bookmark' : 'Bookmark'}
                >
                  <Bookmark className={clsx('h-4 w-4', config.interactions.bookmarked ? 'fill-blue-500 text-blue-500' : '')} />
                </button>
              </div>
            )}
          </div>
        </div>
      </article>
    )
  }

  if (config.platform === 'tiktok') {
    return <VerticalVideoPreview config={config} onInteraction={onInteraction} variant="tiktok" />
  }

  if (config.platform === 'instagram-reels') {
    return <VerticalVideoPreview config={config} onInteraction={onInteraction} variant="instagram-reels" />
  }

  if (config.platform === 'facebook') {
    const currentLikes = config.interactions.liked ? config.metrics.likes + 1 : config.metrics.likes
    const isDark = config.theme === 'dark'
    const mutedText = isDark ? 'text-[#b0b3b8]' : 'text-gray-500'
    const hoverBg = isDark ? 'hover:bg-[#3a3b3c]' : 'hover:bg-gray-100'
    const ringColor = isDark ? 'ring-[#242526]' : 'ring-white'

    return (
      <article className={clsx('mx-auto w-full max-w-xl overflow-hidden rounded-lg border shadow-xl', isDark ? 'border-[#393a3b] bg-[#242526]' : 'border-gray-200 bg-white')}>
        {/* Header */}
        <header className="flex items-start gap-2.5 px-4 py-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-blue-500 to-blue-700">
            {config.avatarUrl ? (
              <Image src={config.avatarUrl} alt={`${config.username} avatar`} width={40} height={40} className="h-full w-full object-cover" unoptimized />
            ) : (
              <Camera className="h-4 w-4 text-white" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-0.5">
              <span className={clsx('text-[15px] font-semibold', isDark ? 'text-[#e4e6eb]' : 'text-gray-900')}>{config.username}</span>
              {config.verified && <FacebookVerifiedBadge />}
            </div>
            <div className={clsx('flex items-center gap-1 text-xs', mutedText)}>
              <span>{config.timestamp}</span>
              <span>·</span>
              <Globe className="h-3 w-3" />
            </div>
          </div>
          <button className={clsx('rounded-full p-1.5', hoverBg)} type="button" aria-label="More options">
            <MoreHorizontal className={clsx('h-5 w-5', mutedText)} />
          </button>
        </header>

        {/* Content */}
        <div className="px-4 pb-3">
          <p className={clsx('text-[15px] whitespace-pre-wrap', isDark ? 'text-[#e4e6eb]' : 'text-gray-900')}>{config.content}</p>
          {config.hashtags && <p className={clsx('mt-1 text-[15px]', isDark ? 'text-[#4599ff]' : 'text-[#1877F2]')}>{config.hashtags}</p>}
        </div>

        {/* Media */}
        {config.mediaUrl ? (
          <Image src={config.mediaUrl} alt="Post media" width={800} height={500} className="w-full object-cover" style={{ maxHeight: '500px', height: 'auto' }} unoptimized />
        ) : (
          <div className={clsx('flex w-full items-center justify-center', isDark ? 'bg-gradient-to-br from-slate-700 to-slate-800' : 'bg-gradient-to-br from-blue-50 to-blue-100')} style={{ height: '360px' }}>
            <ImageIcon className="h-16 w-16 text-gray-300" />
          </div>
        )}

        {/* Reaction summary */}
        {config.showMetrics && (
          <div className={clsx('flex items-center justify-between px-4 py-2 text-[13px]', mutedText)}>
            <div className="flex items-center gap-1">
              <span className="flex -space-x-1">
                <span className={clsx('flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[#1877F2] ring-2', ringColor)}>
                  <ThumbsUp className="h-2.5 w-2.5 fill-white text-white" />
                </span>
                <span className={clsx('flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[#F33E58] text-[9px] ring-2', ringColor)}>
                  ❤️
                </span>
              </span>
              <span className="ml-1">{formatMetric(currentLikes)}</span>
            </div>
            <div className="flex items-center gap-3">
              <span>{formatMetric(config.metrics.comments)} comments</span>
              <span>{formatMetric(config.metrics.shares)} shares</span>
            </div>
          </div>
        )}

        {/* Action bar */}
        <div className={clsx('mx-2 flex items-center justify-between border-t py-1', isDark ? 'border-[#393a3b]' : 'border-gray-200')}>
          <button
            className={clsx('flex flex-1 items-center justify-center gap-2 rounded py-2 transition-colors', hoverBg, config.interactions.liked ? 'text-[#1877F2]' : mutedText)}
            onClick={() => onInteraction('liked')}
            type="button"
            aria-label={config.interactions.liked ? 'Unlike' : 'Like'}
          >
            <ThumbsUp className={clsx('h-5 w-5', config.interactions.liked ? 'fill-[#1877F2]' : '')} />
            <span className="text-sm font-semibold">Like</span>
          </button>
          <button className={clsx('flex flex-1 items-center justify-center gap-2 rounded py-2 transition-colors', mutedText, hoverBg)} type="button" aria-label="Comment">
            <MessageCircle className="h-5 w-5" />
            <span className="text-sm font-semibold">Comment</span>
          </button>
          <button className={clsx('flex flex-1 items-center justify-center gap-2 rounded py-2 transition-colors', mutedText, hoverBg)} type="button" aria-label="Share">
            <Share2 className="h-5 w-5" />
            <span className="text-sm font-semibold">Share</span>
          </button>
        </div>
      </article>
    )
  }

  if (config.platform === 'youtube') {
    const isDark = config.theme === 'dark'
    const title = config.headline.trim().length > 0 ? config.headline : config.content
    const durationBadge = config.duration && config.duration.trim().length > 0 ? config.duration : '12:34'

    return (
      <article className={clsx('mx-auto w-full max-w-md overflow-hidden rounded-xl p-3 shadow-xl', isDark ? 'bg-[#0F0F0F]' : 'bg-white')}>
        {/* Thumbnail */}
        <div className="relative w-full overflow-hidden rounded-xl bg-gray-800" style={{ aspectRatio: '16 / 9' }}>
          {config.mediaUrl ? (
            <Image src={config.mediaUrl} alt="Video thumbnail" fill className="object-cover" sizes="448px" unoptimized />
          ) : (
            // Empty-thumbnail state mirrors YouTube's plain grey slot (the real
            // feed card has no play-button overlay — playback happens on tap).
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-700 to-gray-900">
              <ImageIcon className="h-12 w-12 text-gray-500" />
            </div>
          )}
          <span className="absolute bottom-1.5 right-1.5 rounded bg-black/80 px-1 py-0.5 text-[11px] font-semibold text-white">
            {durationBadge}
          </span>
        </div>

        {/* Info row */}
        <div className="mt-3 flex gap-3">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-red-500 to-rose-700">
            {config.avatarUrl ? (
              <Image src={config.avatarUrl} alt={`${config.username} avatar`} width={36} height={36} className="h-full w-full object-cover" unoptimized />
            ) : (
              <Camera className="h-4 w-4 text-white" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className={clsx('line-clamp-2 text-[14px] font-semibold', isDark ? 'text-white' : 'text-gray-900')}>{title}</p>
            <div className={clsx('mt-0.5 flex flex-wrap items-center gap-x-1 text-xs', isDark ? 'text-gray-400' : 'text-gray-600')}>
              <span className="inline-flex items-center gap-0.5">
                {config.username}
                {config.verified && (
                  <svg viewBox="0 0 24 24" className="h-3 w-3" aria-label="Verified">
                    <circle cx="12" cy="12" r="11" className={isDark ? 'fill-gray-500' : 'fill-gray-400'} />
                    <path d="M10.4 15.2l-2.6-2.6 1.1-1.1 1.5 1.5 4-4 1.1 1.1z" fill="#fff" />
                  </svg>
                )}
              </span>
              {config.showMetrics && <span>· {formatMetric(config.metrics.likes)} views</span>}
              <span>· {config.timestamp}</span>
            </div>
          </div>
          <button className="flex-shrink-0 p-0.5" type="button" aria-label="More">
            <MoreVertical className={clsx('h-4 w-4', isDark ? 'text-gray-400' : 'text-gray-600')} />
          </button>
        </div>
      </article>
    )
  }

  // LinkedIn is the only platform not returned above. This assignment is a
  // compile-time exhaustiveness guard: add a SocialPlatform to the union (and
  // PLATFORM_META) without a render branch here and `config.platform` becomes
  // wider than 'linkedin', failing the build instead of silently rendering as
  // LinkedIn.
  const _exhaustivePlatform: 'linkedin' = config.platform
  void _exhaustivePlatform

  // LinkedIn style
  const currentLikes = config.interactions.liked ? config.metrics.likes + 1 : config.metrics.likes

  return (
    <article className="mx-auto w-full max-w-xl rounded-lg border border-gray-300 bg-white shadow-xl">
      {/* Header */}
      <header className="flex items-start gap-2 px-4 py-3">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-200">
          {config.avatarUrl ? (
            <Image
              src={config.avatarUrl}
              alt={`${config.username} avatar`}
              width={48}
              height={48}
              className="h-full w-full object-cover"
              unoptimized
            />
          ) : (
            <ImageIcon className="h-5 w-5 text-gray-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <span className="text-sm font-semibold text-gray-900">{config.username}</span>
            {config.verified && (
              <svg viewBox="0 0 16 16" className="h-4 w-4" role="img" aria-label="Verified">
                <path fill="#0a66c2" d="M8 1.5A6.5 6.5 0 1 0 14.5 8 6.51 6.51 0 0 0 8 1.5zM6.72 10.78L4.22 8.28a.75.75 0 0 1 1.06-1.06l1.97 1.97 3.47-3.47a.75.75 0 0 1 1.06 1.06z" />
              </svg>
            )}
          </div>
          <p className="text-xs text-gray-600">{config.subtitle}</p>
          <p className="text-xs text-gray-500">{config.timestamp} · 🌐</p>
        </div>
        <button className="p-1.5 hover:bg-gray-100 rounded" type="button">
          <MoreHorizontal className="h-5 w-5 text-gray-600" />
        </button>
      </header>

      {/* Content */}
      <div className="px-4 pb-3">
        <p className="text-sm text-gray-900 whitespace-pre-wrap">{config.content}</p>
        {config.hashtags && (
          <p className="mt-2 text-sm text-blue-700">{config.hashtags}</p>
        )}
      </div>

      {/* Media */}
      {config.mediaUrl && (
        <Image
          src={config.mediaUrl}
          alt="Post media"
          width={800}
          height={500}
          className="w-full object-cover"
          style={{ maxHeight: '500px', height: 'auto' }}
          unoptimized
        />
      )}

      {/* Engagement Summary */}
      {config.showMetrics && (
        <div className="flex items-center justify-between px-4 py-2.5 text-xs text-gray-600">
          <button className="flex items-center gap-1 hover:underline hover:text-blue-700" type="button">
            <div className="flex items-center -space-x-1">
              <div className="flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 border border-white">
                <ThumbsUp className="h-2.5 w-2.5 text-white fill-white" />
              </div>
              <div className="flex h-4 w-4 items-center justify-center rounded-full bg-green-600 border border-white">
                <span className="text-[8px] text-white">💡</span>
              </div>
              <div className="flex h-4 w-4 items-center justify-center rounded-full bg-red-500 border border-white">
                <Heart className="h-2.5 w-2.5 text-white fill-white" />
              </div>
            </div>
            <span className="ml-1">{formatMetric(currentLikes)}</span>
          </button>
          <div className="flex items-center gap-3 text-gray-600">
            <button className="hover:underline hover:text-blue-700" type="button">{formatMetric(config.metrics.comments)} comments</button>
            <span>·</span>
            <button className="hover:underline hover:text-blue-700" type="button">{formatMetric(config.metrics.shares)} reposts</button>
          </div>
        </div>
      )}

      {/* Action Bar */}
      <div className="border-t border-gray-300 px-2 py-1.5 flex items-center justify-between">
        <button
          className={clsx(
            'flex flex-1 items-center justify-center gap-1.5 py-2 hover:bg-gray-100 rounded transition-colors',
            config.interactions.liked ? 'text-blue-600' : 'text-gray-600'
          )}
          onClick={() => onInteraction('liked')}
          type="button"
          aria-label={config.interactions.liked ? 'Unlike' : 'Like'}
        >
          <ThumbsUp className={clsx('h-5 w-5', config.interactions.liked ? 'fill-blue-600' : '')} />
          <span className="text-sm font-semibold">Like</span>
        </button>
        <button className="flex flex-1 items-center justify-center gap-1.5 py-2 text-gray-600 hover:bg-gray-100 rounded transition-colors" type="button" aria-label="Comment">
          <MessageCircle className="h-5 w-5" />
          <span className="text-sm font-semibold">Comment</span>
        </button>
        <button className="flex flex-1 items-center justify-center gap-1.5 py-2 text-gray-600 hover:bg-gray-100 rounded transition-colors" type="button" aria-label="Repost">
          <Repeat2 className="h-5 w-5" />
          <span className="text-sm font-semibold">Repost</span>
        </button>
        <button className="flex flex-1 items-center justify-center gap-1.5 py-2 text-gray-600 hover:bg-gray-100 rounded transition-colors" type="button" aria-label="Send">
          <Send className="h-5 w-5" />
          <span className="text-sm font-semibold">Send</span>
        </button>
      </div>
    </article>
  )
}
